import {
  // IconBell,  // restore with the Notification Preferences entry below
  IconBuildingCommunity,
  IconBuildingSkyscraper,
  IconLogout,
  IconSettings,
  IconUserCircle,
} from '@tabler/icons-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Avatar from 'components/common/Avatar';
import { ROUTES } from 'constants/routePaths';
import { useAuth } from 'hooks/useAuth';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { isCenterAdminRole, isMasterRole, isSuperAdminRole } from 'util/access';
import { resolveMediaUrl } from 'util/media';

/** The actual uploaded photo when available, falling back to initials. */
function HeaderAvatar({ name, photoUrl, size }) {
  if (photoUrl) {
    return (
      <img
        src={resolveMediaUrl(photoUrl)}
        alt={name}
        className="flex-shrink-0 rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return <Avatar name={name} size={size} ink />;
}

/** Header avatar that opens a dropdown with the profile summary + Log out. */
export function UserMenu() {
  const { user, role, principal, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Panel switching (mentor ↔ jury) is DISABLED — a multi-role user now sees a
  // single merged panel with every mentor AND jury module together, so there is
  // nothing to switch to. Left here (commented) for an easy re-enable.
  // const [switching, setSwitching] = useState(false);
  // const PANEL_LABEL = { mentor: 'Mentor', jury: 'Jury' };
  // const assignments = Array.isArray(user?.assignments) ? user.assignments : [];
  // const currentAssignment =
  //   user?.activeAssignment ||
  //   (String(role).toLowerCase().includes('jury') ? 'jury' : String(role).toLowerCase() === 'mentor' ? 'mentor' : null);
  // const switchable = assignments.filter(
  //   (a) => PANEL_LABEL[String(a).toLowerCase()] && String(a).toLowerCase() !== String(currentAssignment).toLowerCase(),
  // );
  // const handleSwitch = (assignment) => async () => {
  //   if (switching) return;
  //   setSwitching(true);
  //   try {
  //     await switchPanel(assignment);
  //     setOpen(false);
  //     navigate('/dashboard', { replace: true });
  //   } catch {
  //     /* stay put; the current panel is unchanged on failure */
  //   } finally {
  //     setSwitching(false);
  //   }
  // };

  useOnClickOutside(ref, () => setOpen(false));

  const displayName = user?.name || 'User';
  const avatarUrl = user?.avatarUrl || user?.avatar_url || user?.photoUrl || '';
  // Flag-based (rename-safe): the "Center Profile" shortcut is for center admins.
  const isCenterAdmin = isCenterAdminRole(principal);
  // The super admin's equivalent entry point — same slot in the menu, the
  // tenant one tier up. Not the master: they own every organization but belong
  // to none, so a "my organization" screen has nothing to show them.
  const menuRole = principal?.role || role;
  const isSuperAdmin = isSuperAdminRole(menuRole) && !isMasterRole(menuRole);

  const go = (path) => () => {
    setOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN, { replace: true });
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex items-center rounded-full p-1 transition-colors hover:bg-black/5"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <HeaderAvatar name={displayName} photoUrl={avatarUrl} size={36} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-20 mt-2 w-64 overflow-hidden rounded-xl border border-hair bg-surface shadow-lg"
        >
          <div className="flex items-center gap-3 border-b border-hair px-4 py-3">
            <HeaderAvatar name={displayName} photoUrl={avatarUrl} size={40} />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-ink">{displayName}</p>
              {user?.email && <p className="truncate text-xs text-slate">{user.email}</p>}
              {role && (
                <p className="truncate text-[10px] font-semibold uppercase tracking-wide text-brand-dark">
                  {role.replace(/_/g, ' ')}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={go(ROUTES.PROFILE)}
            className="flex w-full items-center gap-2 px-4 py-3 text-sm text-ink transition-colors hover:bg-mist"
          >
            <IconUserCircle className="h-5 w-5 text-slate" />
            My Profile
          </button>
          {isCenterAdmin && (
            <button
              type="button"
              role="menuitem"
              onClick={go(ROUTES.CENTER_PROFILE)}
              className="flex w-full items-center gap-2 px-4 py-3 text-sm text-ink transition-colors hover:bg-mist"
            >
              <IconBuildingSkyscraper className="h-5 w-5 text-slate" />
              Center Profile
            </button>
          )}
          {/* Notification Preferences — UNDER CONSTRUCTION, withdrawn
              2026-09-11. Its route and lazy import are commented out in
              routes/AppRoutes.jsx; restore all three together. */}
          {/*
          {isCenterAdmin && (
            <button
              type="button"
              role="menuitem"
              onClick={go(ROUTES.CENTER_NOTIFICATION_PREFERENCES)}
              className="flex w-full items-center gap-2 px-4 py-3 text-sm text-ink transition-colors hover:bg-mist"
            >
              <IconBell className="h-5 w-5 text-slate" />
              Notification Preferences
            </button>
          )}
          */}
          {isSuperAdmin && (
            <button
              type="button"
              role="menuitem"
              onClick={go(ROUTES.ORGANIZATION_PROFILE)}
              className="flex w-full items-center gap-2 px-4 py-3 text-sm text-ink transition-colors hover:bg-mist"
            >
              <IconBuildingCommunity className="h-5 w-5 text-slate" />
              Organization Profile
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            onClick={go(ROUTES.SETTINGS)}
            className="flex w-full items-center gap-2 px-4 py-3 text-sm text-ink transition-colors hover:bg-mist"
          >
            <IconSettings className="h-5 w-5 text-slate" />
            Settings &amp; Activities
          </button>
          {/* Panel-switch menu item disabled — the merged mentor/jury panel already
              shows every module, so switching is unnecessary. Kept for re-enable:
          {switchable.length > 0 && (
            <>
              <div className="border-t border-hair" />
              {switchable.map((a) => (
                <button
                  key={a}
                  type="button"
                  role="menuitem"
                  disabled={switching}
                  onClick={handleSwitch(a)}
                  className="flex w-full items-center gap-2 px-4 py-3 text-sm text-ink transition-colors hover:bg-mist disabled:opacity-60"
                >
                  <IconArrowsLeftRight className="h-5 w-5 text-brand-dark" />
                  {switching ? 'Switching…' : `Switch to ${PANEL_LABEL[String(a).toLowerCase()]} Panel`}
                </button>
              ))}
            </>
          )} */}
          <div className="border-t border-hair" />
          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="flex w-full items-center gap-2 px-4 py-3 text-sm text-danger transition-colors hover:bg-danger/5"
          >
            <IconLogout className="h-5 w-5" />
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

export default UserMenu;
